/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { PropsWithChildren } from "react";

import { css, cx } from "@panda/css";
import { SystemStyleObject } from "@panda/types";
import { CosmosTagProps, CosmosText, TagList, Wrap } from "@components/elements";
import type { BlogTagFragment } from "@cosmos/hygraph-schema";
import { HStack } from "@panda/jsx";

const AttributionContainerStyles: SystemStyleObject = {
	display: "flex",
	position: "relative",
	alignItems: "center",
	justifyContent: "center",
	objectFit: "contain",
	h: "full",
	w: "full",
	"& > img, & > video": {
		maxHeight: "full",
		maxWidth: "full",
	},
};

const attributionPosition = 16;

const TagAttributionStackStyle: SystemStyleObject = {
	position: "absolute",
	insetBlockEnd: `eds.dimension.${attributionPosition}`,
	insetInlineEnd: `eds.dimension.${attributionPosition}`,
	width: `calc(100% - (2 * token(spacing.eds.dimension.${attributionPosition})))`,
};

const TagListStyle: SystemStyleObject = {
	"&&": {
		gap: "eds.dimension.8",
		flexShrink: 0,
		justifySelf: "flex-start",
		minHeight: "eds.dimension.24",
		"& .tag": {
			zIndex: "2",
			backdropFilter: "blur(calc(75px / 2))",
			backgroundColor: "eds.material.300",
			paddingBlock: "0",
			"& svg": {
				width: "eds.dimension.16!",
				height: "eds.dimension.16!",
			},
		},
	},
};

const AttributionStyle: SystemStyleObject = {
	minHeight: "eds.dimension.24",
	paddingBlock: "eds.dimension.3",
	paddingInline: "eds.dimension.8",
	borderRadius: "eds.borderRadius.4",
	borderEndEndRadius: `calc(var(--asset-border-radius, 20px) - ${attributionPosition}px) !important`,
	wordWrap: "anywhere",

	marginInlineStart: "auto",
	alignItems: "center",
	textShadow: "0px 2px 4px rgba(0, 0, 0, 0.20)",

	color: "eds.text.contrast",
	bgColor: "rgba(48, 48, 52, 0.4)",
	backdropFilter: "blur(50px)",
	lineClamp: "3",
	overflow: "hidden",
	mdDown: {
		lineClamp: "2",
	},
};

export type CosmosTagsAndAttributionProps = PropsWithChildren<{
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	attribution?: string | null;
	className?: string;
	wrapperCss?: SystemStyleObject;
}>;

export const CosmosTagsAndAttribution = ({
	tags,
	attribution,
	className,
	wrapperCss,
	children,
}: CosmosTagsAndAttributionProps): React.JSX.Element | null => {
	return (
		<Wrap
			condition={!!attribution || !!tags?.length}
			wrap={(children) => (
				<div className={cx("cosmos-attribution-wrapper", className, css(AttributionContainerStyles, wrapperCss))}>
					{children}
					<HStack
						className={css(TagAttributionStackStyle)}
						gap="eds.dimension.16"
						justifyContent="space-between"
						alignItems="flex-end"
					>
						<TagList className={css(TagListStyle)} tags={tags} size="uiSM" />
						<CosmosText className={cx("cosmos-attribution", css(AttributionStyle))} variant="uiSM" text={attribution} />
					</HStack>
				</div>
			)}
		>
			{children}
		</Wrap>
	);
};
